import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { heroContent, visionContent, servicesContent, statsContent, partnersContent } from '../mockData';
import { Button } from '../components/ui/button';
import ServiceModal from '../components/ServiceModal';

const Home = () => {
  const [visibleSections, setVisibleSections] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const sectionRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => [...new Set([...prev, entry.target.dataset.section])]);
          }
        });
      },
      { threshold: 0.1 }
    );

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleServiceClick = (service) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedService(null), 300);
  };

  return (
    <div className="bg-black min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${heroContent.backgroundImage})`,
            filter: 'grayscale(100%) brightness(0.4)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black" />
        
        <div className="relative z-10 text-center px-6 max-w-5xl">
          <div 
            className="opacity-0 animate-fade-in"
            style={{ animation: 'fadeIn 1s ease-out forwards' }}
          >
            <p className="text-white/90 text-sm md:text-base tracking-[0.3em] mb-6 uppercase">
              {heroContent.tagline}
            </p>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-8 leading-tight tracking-tight">
              {heroContent.title}
            </h1>
            <Button 
              size="lg" 
              onClick={() => scrollToSection('services')}
              className="bg-white text-black hover:bg-gray-200 transition-all duration-300 transform hover:scale-105 px-8 py-6 text-base"
            >
              Explore Our Work
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronRight size={32} className="text-white rotate-90" />
        </div>
      </section>

      {/* Vision Section - 3D Effect */}
      <section 
        ref={(el) => (sectionRefs.current[0] = el)}
        data-section="vision"
        id="about"
        className="py-32 px-6 bg-black relative overflow-hidden"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div 
              className={`transition-all duration-1000 transform ${
                visibleSections.includes('vision') 
                  ? 'opacity-100 translate-x-0' 
                  : 'opacity-0 -translate-x-10'
              }`}
            >
              <h2 className="text-sm tracking-[0.3em] text-white/70 mb-4 uppercase">
                {visionContent.title}
              </h2>
              <h3 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                {heroContent.title}
              </h3>
              <p className="text-gray-400 text-lg leading-relaxed">
                {visionContent.description}
              </p>
            </div>
            <div 
              className={`relative transition-all duration-1000 transform delay-300 ${
                visibleSections.includes('vision') 
                  ? 'opacity-100 translate-x-0 scale-100' 
                  : 'opacity-0 translate-x-10 scale-95'
              }`}
            >
              {/* 3D Container Effect */}
              <div className="relative perspective-1000">
                {/* Shadow layers for depth */}
                <div className="absolute inset-0 bg-gradient-to-br from-gray-800/30 to-gray-900/30 rounded-sm transform translate-x-6 translate-y-6 blur-xl" />
                <div className="absolute inset-0 bg-gradient-to-br from-gray-700/20 to-gray-800/20 rounded-sm transform translate-x-4 translate-y-4 blur-lg" />
                
                {/* Main image with 3D effect */}
                <div className="relative group overflow-hidden rounded-sm border border-white/10 shadow-2xl transform transition-all duration-700 hover:scale-105 hover:rotate-1">
                  <img
                    src={visionContent.image}
                    alt="Vision"
                    className="w-full h-[550px] object-cover transform group-hover:scale-110 transition-transform duration-700"
                    style={{ filter: 'grayscale(30%)' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60" />
                  
                  {/* Highlight effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Full Width Stacked */}
      <section 
        ref={(el) => (sectionRefs.current[1] = el)}
        data-section="services"
        id="services"
        className="bg-black"
      >
        <div className="text-center py-24 px-6">
          <h2 className="text-sm tracking-[0.3em] text-white/70 mb-4 uppercase">OUR SERVICES</h2>
          <h3 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Comprehensive Industrial Design Solutions
          </h3>
        </div>

        {servicesContent.map((service, index) => (
          <div 
            key={service.id}
            className={`relative min-h-screen flex items-center overflow-hidden transition-all duration-1000 ${
              visibleSections.includes('services') 
                ? 'opacity-100' 
                : 'opacity-0'
            }`}
            style={{ transitionDelay: `${index * 300}ms` }}
          >
            {/* Background Image */}
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ 
                backgroundImage: `url(${service.image})`,
                // filter: 'grayscale(100%) brightness(0.6)'
              }}
            />
            
            {/* Gradient Overlay */}
            <div className={`absolute inset-0 bg-gradient-to-r ${
              index % 2 === 0 ? 'from-black via-black/90 to-transparent' : 'from-transparent via-black/90 to-black'
            }`} />

            {/* Content */}
            <div className="relative z-10 w-full">
              <div className={`max-w-7xl mx-auto px-6 py-24 ${
                index % 2 === 0 ? 'text-left' : 'text-right'
              }`}>
                <div className={`max-w-2xl ${index % 2 === 0 ? '' : 'ml-auto'}`}>
                  <div className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-sm mb-6">
                    <span className="text-white/90 text-sm tracking-widest uppercase">
                      Service {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                  
                  <h3 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight tracking-tight">
                    {service.title}
                  </h3>
                  
                  <p className="text-gray-300 text-xl leading-relaxed mb-8">
                    {service.description}
                  </p>
                  
                  <Button 
                    size="lg"
                    onClick={() => handleServiceClick(service)}
                    className="bg-white text-black hover:bg-gray-200 transition-all duration-300 transform hover:scale-105 px-8 py-6 text-base group"
                  >
                    Read More
                    <ArrowRight className="ml-2 transform group-hover:translate-x-2 transition-transform" size={20} />
                  </Button>
                </div>
              </div>
            </div>

            {/* Decorative Line */}
            {index < servicesContent.length - 1 && (
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-px h-24 bg-gradient-to-b from-white/50 to-transparent" />
            )}
          </div>
        ))}
      </section>

      {/* Stats Section - Enhanced with 3D Elements */}
      <section 
        ref={(el) => (sectionRefs.current[2] = el)}
        data-section="stats"
        className="relative overflow-hidden min-h-screen flex items-center"
      >
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1560461396-ec0ef7bb29dd?w=1920)',
            filter: 'grayscale(100%) brightness(0.25)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black" />
        
        {/* Animated background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <h2 className="text-5xl md:text-7xl font-bold text-white text-center mb-8 tracking-tight">
            Elemento Design
          </h2>
          <p className="text-gray-400 text-center text-xl mb-24 max-w-2xl mx-auto">
            Delivering excellence through innovation and precision
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {statsContent.map((stat, index) => (
              <div 
                key={index}
                className={`relative group transition-all duration-1000 transform ${
                  visibleSections.includes('stats') 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                {/* Card with 3D effect */}
                <div className="relative p-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-sm hover:bg-white/10 transition-all duration-500 transform hover:scale-110 hover:-translate-y-2">
                  {/* Glow effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-sm" />
                  
                  <div className="relative">
                    <div className="text-6xl md:text-7xl font-bold text-white mb-4 group-hover:scale-110 transition-transform duration-500">
                      {stat.value}
                    </div>
                    <div className="text-xs tracking-[0.3em] text-gray-300 uppercase">
                      {stat.label}
                    </div>
                  </div>
                  
                  {/* Corner decoration */}
                  <div className="absolute top-0 right-0 w-20 h-20 border-t-2 border-r-2 border-white/20 rounded-tr-sm" />
                  <div className="absolute bottom-0 left-0 w-20 h-20 border-b-2 border-l-2 border-white/20 rounded-bl-sm" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Section - Enhanced with Images */}
      <section 
        ref={(el) => (sectionRefs.current[3] = el)}
        data-section="partners"
        className="relative py-32 px-6 bg-white overflow-hidden min-h-screen flex items-center"
      >
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto w-full">
          <div className="text-center mb-20">
            <h2 className="text-sm tracking-[0.3em] text-black/70 mb-4 uppercase">INDUSTRY COLLABORATION</h2>
            <h3 className="text-5xl md:text-7xl font-bold text-black mb-8 tracking-tight">Our Partners</h3>
            <p className="text-gray-600 text-xl mb-8 max-w-3xl mx-auto leading-relaxed">
              Collaborate with industry leaders to drive innovation and create groundbreaking products. 
              Our partners share our vision of impactful and sustainable design solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {partnersContent.map((partner, index) => (
              <div 
                key={index}
                className={`relative group transition-all duration-1000 transform ${
                  visibleSections.includes('partners') 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                {/* 3D Card with depth */}
                <div className="relative">
                  {/* Shadow layers */}
                  <div className="absolute inset-0 bg-gray-300 rounded-sm transform translate-x-4 translate-y-4 transition-transform duration-300 group-hover:translate-x-6 group-hover:translate-y-6" />
                  <div className="absolute inset-0 bg-gray-200 rounded-sm transform translate-x-2 translate-y-2 transition-transform duration-300 group-hover:translate-x-3 group-hover:translate-y-3" />
                  
                  {/* Main card */}
                  <div className="relative p-8 bg-white border-2 border-black rounded-sm transition-all duration-300 transform group-hover:-translate-y-2 group-hover:shadow-2xl cursor-pointer h-80 flex flex-col">
                    <div className="flex flex-col items-center justify-center flex-1">
                      {/* Partner Image */}
                      <div className="w-24 h-24 mb-6 flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 flex-shrink-0 overflow-hidden rounded-sm">
                        <img
                          src={partner.image}
                          alt={partner.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      
                      {/* Text */}
                      <p className="text-sm font-bold text-black tracking-wider text-center uppercase leading-relaxed h-16 flex items-center">
                        {partner.name}
                      </p>
                    </div>
                    
                    {/* Corner accents */}
                    <div className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-black opacity-50" />
                    <div className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 border-black opacity-50" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Modal */}
      {selectedService && (
        <ServiceModal 
          service={selectedService}
          isOpen={isModalOpen}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default Home;